/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import type { CSSProperties } from 'react';

/**
 * A chip painted in a colour **somebody chose**, rather than one this app owns.
 *
 * Circle colours and tag colours come out of the database. Nobody picked them
 * against a dark background, and a fair number of them are dark: the default
 * offered when a circle is created is a mid-indigo, and people reach for navy,
 * maroon and forest green because they look good on the white board they were
 * looking at. Printed unchanged on a dark card, those are a hole where a word
 * should be — and the word is the entire reason the badge exists.
 *
 * So the colour is handed to CSS as a **custom property** instead of being
 * baked into three inline declarations, and `index.css` decides what to do with
 * it per theme: used as-is in light, mixed toward white in dark, with the fill
 * and the edge carrying more of it to compensate. One `color-mix` per theme
 * replaces a decision that cannot be made in JavaScript, because at render time
 * we do not know which theme is on and should not have to.
 *
 * The alternative was computing a readable variant in JS from the stored hex.
 * It would work, and it would mean every one of these chips re-deriving a colour
 * on every render, plus a second copy of the same arithmetic for whatever gets
 * added next. This is one line at each call site and none at runtime.
 */
export function chip(color: string): CSSProperties {
  // `--chip` is not a known CSS property, so the cast is unavoidable; React
  // passes custom properties through untouched.
  return { '--chip': color } as CSSProperties;
}
